import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  Users,
  Search,
  Filter,
  Settings,
  CheckCircle,
  XCircle,
  DollarSign,
  X
} from 'lucide-react';
import { apiService } from '../../services/api';
import { EmployeeWithAssignments, EmployeeFilters } from '../../types/employee';
import { EmployeeCard } from './EmployeeCard';
import { AssignmentModal } from './AssignmentModal';
import { EmployeeModal } from './EmployeeModal';
import { UnassignModal } from './UnassignModal';

export const EmployeeManagement: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState<EmployeeFilters>({
    page: 1,
    per_page: 1000, // Fetch all employees for client-side filtering
    search: '', // Remove from API filters
    role: '',
    is_active: true, // Show only active users by default
    has_accounts: true, // Show only assigned users by default
    account_id: undefined
  });

  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeWithAssignments | null>(null);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [showUnassignModal, setShowUnassignModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const queryClient = useQueryClient();

  // Fetch employee statistics (separate endpoint for accurate counts)
  const { data: statsData } = useQuery({
    queryKey: ['employee-stats'],
    queryFn: () => apiService.getEmployeeStats(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch employees (without search parameter - we'll filter client-side)
  const {
    data: employeesData,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['employees', { ...filters, search: undefined }],
    queryFn: () => {
      const { search, ...apiFilters } = filters;
      return apiService.getEmployees(apiFilters);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch accounts for filter dropdown
  const { data: accountsData } = useQuery({
    queryKey: ['accounts-for-filter'],
    queryFn: () => apiService.getAccounts({ per_page: 1000 }), // Get all accounts for filter
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  // Initialize filters from URL parameters
  useEffect(() => {
    const accountId = searchParams.get('account_id');
    const role = searchParams.get('role');
    const hasAccounts = searchParams.get('has_accounts');
    const isActive = searchParams.get('is_active');

    setFilters(prev => ({
      ...prev,
      account_id: accountId ? parseInt(accountId) : undefined,
      role: role || '',
      has_accounts: hasAccounts === 'true' ? true : hasAccounts === 'false' ? false : prev.has_accounts,
      is_active: isActive === 'true' ? true : isActive === 'false' ? false : prev.is_active,
    }));
  }, [searchParams]);

  // Client-side filtering of employees based on search term
  const filteredEmployees = useMemo(() => {
    const allEmployees = employeesData?.data || [];

    if (!clientSearchTerm.trim()) {
      return allEmployees;
    }

    const searchLower = clientSearchTerm.toLowerCase();
    return allEmployees.filter((employee: EmployeeWithAssignments) =>
      employee.name.toLowerCase().includes(searchLower) ||
      employee.email.toLowerCase().includes(searchLower)
    );
  }, [employeesData?.data, clientSearchTerm]);

  const handleFilterChange = (key: keyof EmployeeFilters, value: any) => {
    const newFilters = {
      ...filters,
      [key]: value,
      page: 1 // Reset to first page when filtering
    };

    setFilters(newFilters);

    // Update URL parameters (exclude search since it's client-side)
    const newSearchParams = new URLSearchParams();
    if (newFilters.account_id) newSearchParams.set('account_id', newFilters.account_id.toString());
    if (newFilters.role) newSearchParams.set('role', newFilters.role);
    if (newFilters.has_accounts !== undefined) newSearchParams.set('has_accounts', newFilters.has_accounts.toString());
    if (newFilters.is_active !== undefined) newSearchParams.set('is_active', newFilters.is_active.toString());

    setSearchParams(newSearchParams);
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const clearFilters = () => {
    setClientSearchTerm('');
    setFilters({
      page: 1,
      per_page: 1000,
      search: '',
      role: '',
      is_active: true, // Show only active users by default
      has_accounts: true, // Show only assigned users by default
      account_id: undefined
    });
    setSearchParams({});
  };

  const openAssignmentModal = (employee: EmployeeWithAssignments) => {
    setSelectedEmployee(employee);
    setShowAssignmentModal(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <XCircle className="h-5 w-5 text-red-400 mr-2" />
          <span className="text-red-800">Failed to load employees</span>
        </div>
      </div>
    );
  }

  const employees = filteredEmployees; // Use filtered employees for display
  const pagination = employeesData?.meta;

  // Statistics (from dedicated stats endpoint - accurate counts regardless of filters)
  const stats = statsData?.data;
  const totalEmployees = stats?.total_employees || 0;
  const activeEmployees = stats?.active_employees || 0;
  const employeesWithAssignments = stats?.employees_with_assignments || 0;
  const employeesWithCommissions = stats?.employees_with_commissions || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Employee Management</h1>
          <p className="text-gray-600">
            Manage employee account assignments and commission structures
          </p>
        </div>

      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Total Employees</p>
              <p className="text-2xl font-semibold text-gray-900">{totalEmployees}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Active</p>
              <p className="text-2xl font-semibold text-gray-900">{activeEmployees}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Settings className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">With Assignments</p>
              <p className="text-2xl font-semibold text-gray-900">{employeesWithAssignments}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <DollarSign className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">With Commissions</p>
              <p className="text-2xl font-semibold text-gray-900">{employeesWithCommissions}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="h-5 w-5 text-gray-400 absolute left-3 top-3" />
                <input
                  type="text"
                  placeholder="Search employees by name or email (instant filter)..."
                  value={clientSearchTerm}
                  onChange={(e) => setClientSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {clientSearchTerm && (
                  <button
                    onClick={() => setClientSearchTerm('')}
                    className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                    title="Clear search"
                  >
                    <X className="h-5 w-5" />
                  </button>
                )}
              </div>
            </div>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <Filter className="h-4 w-4 mr-2" />
              Filters
              {(filters.role || filters.has_accounts !== undefined || filters.is_active === false || filters.account_id) && (
                <span className="ml-2 bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">
                  Active
                </span>
              )}
            </button>
          </div>

          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Account Filter</label>
                  <select
                    value={filters.account_id || ''}
                    onChange={(e) => handleFilterChange('account_id', e.target.value ? parseInt(e.target.value) : undefined)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  >
                    <option value="">All Accounts</option>
                    {accountsData?.data?.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.account_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                  <select
                    value={filters.role || ''}
                    onChange={(e) => handleFilterChange('role', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  >
                    <option value="">All Roles</option>
                    <option value="super_admin">Super Admin</option>
                    <option value="admin">Admin</option>
                    <option value="user">User</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    value={filters.is_active === undefined ? '' : filters.is_active.toString()}
                    onChange={(e) => handleFilterChange('is_active', e.target.value === '' ? undefined : e.target.value === 'true')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  >
                    <option value="">All Statuses</option>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Assignments</label>
                  <select
                    value={filters.has_accounts === undefined ? '' : filters.has_accounts.toString()}
                    onChange={(e) => handleFilterChange('has_accounts', e.target.value === '' ? undefined : e.target.value === 'true')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  >
                    <option value="">All Employees</option>
                    <option value="true">With Assignments</option>
                    <option value="false">No Assignments</option>
                  </select>
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Employee Grid */}
      {employees.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No employees found</h3>
          <p className="text-gray-600">
            {clientSearchTerm ? 'Try adjusting your search criteria' : 'No employees available in the system'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {employees.map((employee: EmployeeWithAssignments) => (
            <EmployeeCard
              key={employee.id}
              employee={employee}
              onAssign={() => openAssignmentModal(employee)}
              onUnassign={() => {
                setSelectedEmployee(employee);
                setShowUnassignModal(true);
              }}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.last_page > 1 && (
        <div className="flex items-center justify-between bg-white px-6 py-4 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-gray-700">
            Showing {pagination.from} to {pagination.to} of {pagination.total} results
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(pagination.current_page - 1)}
              disabled={pagination.current_page === 1}
              className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>

            {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
              const page = i + 1;
              const isActive = page === pagination.current_page;

              return (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`px-3 py-1 text-sm rounded-md ${
                    isActive
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {page}
                </button>
              );
            })}

            <button
              onClick={() => handlePageChange(pagination.current_page + 1)}
              disabled={pagination.current_page === pagination.last_page}
              className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      {showAssignmentModal && selectedEmployee && (
        <AssignmentModal
          employee={selectedEmployee}
          onClose={() => {
            setShowAssignmentModal(false);
            setSelectedEmployee(null);
          }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['employees'] });
            refetch();
          }}
        />
      )}

      {showEmployeeModal && (
        <EmployeeModal
          employee={selectedEmployee}
          onClose={() => {
            setShowEmployeeModal(false);
            setSelectedEmployee(null);
          }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['employees'] });
            refetch();
          }}
        />
      )}

      {showUnassignModal && selectedEmployee && (
        <UnassignModal
          employee={selectedEmployee}
          onClose={() => {
            setShowUnassignModal(false);
            setSelectedEmployee(null);
          }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['employees'] });
            refetch();
            setShowUnassignModal(false);
            setSelectedEmployee(null);
          }}
        />
      )}
    </div>
  );
};